import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box, Typography, Divider, Stack, Alert, Button, Skeleton, Checkbox,
  FormControlLabel, Link as MuiLink, CircularProgress, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useNexusBilling } from '../context/NexusBillingContext';
import { billingApi, formatPaise, describeLineUnits } from '../api/billingApi';
import QuantityStepper from '../components/NexusQuantityStepper';
import {
  TOKENS, PageContainer, PageHeader, SectionCard, EmptyState, GradientButton, fadeUp,
} from '../components/NextGenPortalUI';

/**
 * NextGenCheckout — /nextgen-builder/checkout
 *
 * The last look at the order, then Razorpay. The server owns every number:
 * the cart is shown as-is, and the moment /billing/checkout answers, the
 * summary switches to the order the backend actually created (prices can
 * legitimately move between adding to the cart and paying — e.g. a pricing
 * tier filling up). Entitlements are granted only after server-side
 * verification of the payment signature.
 *
 * 27-Aug-2026: quantities are editable here too (same stepper as the cart,
 * same PATCH/DELETE cart APIs, totals re-rendered from the server's reply).
 * Editing after a checkout attempt discards that pending order: the backend
 * returns the SAME order for the same idempotency key, so the key is rotated
 * and the view falls back to the live cart until "Proceed to Payment" creates
 * a fresh order from the updated cart.
 */

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const newIdempotencyKey = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  } catch (_) { /* fall through */ }
  const rand = () => Math.random().toString(16).slice(2, 10);
  return `nxc-${Date.now().toString(16)}-${rand()}${rand()}`;
};

const readUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null') || {}; } catch (_) { return {}; }
};

const SummaryLine = ({ label, value, strong }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
    <Typography variant={strong ? 'body1' : 'body2'} sx={{ color: strong ? TOKENS.text.primary : TOKENS.text.muted, fontWeight: strong ? 800 : 500 }}>
      {label}
    </Typography>
    <Typography
      variant={strong ? 'h6' : 'body2'}
      sx={{ color: TOKENS.text.primary, fontWeight: strong ? 900 : 700, letterSpacing: strong ? '-0.02em' : 0 }}
    >
      {value}
    </Typography>
  </Box>
);

const NextGenCheckout = () => {
  const navigate = useNavigate();
  const { cart, catalog, loading, refreshAll, updateCartItem, removeCartItem } = useNexusBilling();

  const idempotencyKey = useRef(newIdempotencyKey());
  const [terms, setTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [failure, setFailure] = useState('');
  const [serverOrder, setServerOrder] = useState(null);
  const [busyItemId, setBusyItemId] = useState(null);
  const [cartError, setCartError] = useState('');

  const user = useMemo(readUser, []);
  const items = useMemo(() => (cart && Array.isArray(cart.items) ? cart.items : []), [cart]);
  const hasUnavailable = items.some((i) => i.unavailable);

  /* Once checkout has answered, the order the server created is the truth. */
  const view = useMemo(() => {
    if (serverOrder) {
      return {
        lines: (serverOrder.items || []).map((i) => ({
          id: i.id,
          productName: i.productName,
          technologyTitle: i.technologyTitle,
          quantity: i.quantity,
          unitsTotal: i.unitsTotal,
          unitsPerPurchase: i.unitsPerPurchase,
          accessType: i.accessType,
          validityLabel: i.validityLabel,
          unitPricePaise: i.unitPricePaise,
          subtotalPaise: i.subtotalPaise,
          cartItemId: (items.find((c) => c.productCode === i.productCode && (c.technologySlug || null) === (i.technologySlug || null)) || {}).id || null,
          productCode: i.productCode,
          unavailable: false,
        })),
        subtotalPaise: serverOrder.subtotalPaise,
        taxPaise: serverOrder.taxPaise,
        grandTotalPaise: serverOrder.grandTotalPaise,
        taxLabel: `${serverOrder.taxCode || 'Tax'} (${serverOrder.taxPercent}%)`,
        fromServer: true,
      };
    }
    if (!cart || !cart.totals) return null;
    const taxRate = cart.taxRate || (catalog ? catalog.taxRate : null);
    return {
      lines: items.map((i) => ({
        id: i.id,
        productName: i.productName,
        technologyTitle: i.technologyTitle,
        quantity: i.quantity,
        unitsTotal: i.unitsTotal,
        unitsPerPurchase: i.unitsPerPurchase,
        accessType: i.accessType,
        validityLabel: i.validityLabel,
        unitPricePaise: i.unitPricePaise,
        subtotalPaise: i.subtotalPaise,
        cartItemId: i.id,
        productCode: i.productCode,
        unavailable: !!i.unavailable,
      })),
      subtotalPaise: cart.totals.subtotalPaise,
      taxPaise: cart.totals.taxPaise,
      grandTotalPaise: cart.totals.grandTotalPaise,
      taxLabel: taxRate ? `${taxRate.name || taxRate.code} (${taxRate.percent}%)` : 'Tax',
      fromServer: false,
    };
  }, [serverOrder, cart, catalog, items]);

  const handleAuthRequired = useCallback(() => {
    navigate('/nextgen-builder/login', { state: { from: '/nextgen-builder/checkout' } });
  }, [navigate]);

  /** maxQuantityPerOrder lives on the catalog product, not on the cart line (same rule as the cart page). */
  const maxQtyFor = useCallback((productCode) => {
    if (!catalog || !Array.isArray(catalog.features)) return null;
    for (const f of catalog.features) {
      const p = (f.products || []).find((x) => x.code === productCode);
      if (p && p.maxQuantityPerOrder) return p.maxQuantityPerOrder;
    }
    return null;
  }, [catalog]);

  /**
   * Cart mutation from the checkout page. Any edit invalidates a pending order
   * created by an earlier "Proceed to Payment": the summary returns to the live
   * cart and the next checkout gets a new idempotency key, so the backend
   * builds a fresh order from the updated cart instead of replaying the old one.
   */
  const runCartMutation = useCallback(async (itemId, fn) => {
    if (busy) return;
    setBusyItemId(itemId);
    setCartError('');
    try {
      await fn();
      if (serverOrder) {
        setServerOrder(null);
        idempotencyKey.current = newIdempotencyKey();
        setFailure('');
      }
    } catch (e) {
      if (e && e.status === 401) { handleAuthRequired(); return; }
      setCartError((e && e.message) || 'Could not update your cart');
    } finally {
      setBusyItemId(null);
    }
  }, [busy, serverOrder, handleAuthRequired]);
  const handleQuantity = (line, quantity) => runCartMutation(line.id, () => updateCartItem(line.id, quantity));
  const handleRemove = (line) => runCartMutation(line.id, () => removeCartItem(line.id));
  // Once the payment window is open the order is fixed; after a failure/dismissal edits are allowed again.
  const canEdit = !busy;
  const itemBusy = busyItemId != null;

  const openRazorpay = useCallback((order, razorpay) => {
    const options = {
      key: razorpay.keyId,
      amount: razorpay.amountPaise,
      currency: razorpay.currency,
      order_id: razorpay.orderId,
      name: 'The Skill Genie',
      description: `TSG Nexus — ${order.orderNumber}`,
      image: 'https://d1atm4dw3zgpab.cloudfront.net/assets/tsg-logo.png',
      theme: { color: TOKENS.brand.primary },
      prefill: {
        name: user.fullName || user.name || '',
        email: user.email || '',
        contact: user.phone || user.mobile || user.contact || '',
      },
      handler: async (response) => {
        try {
          await billingApi.verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          await refreshAll();
          navigate(`/nextgen-builder/orders/${order.id}`);
        } catch (e) {
          setBusy(false);
          if (e && e.status === 401) { handleAuthRequired(); return; }
          setFailure((e && e.message) || 'We could not verify this payment. If money was deducted, it will be reconciled automatically — please check your orders in a few minutes.');
        }
      },
      modal: {
        ondismiss: () => {
          setBusy(false);
          billingApi
            .reportPaymentFailure({ razorpay_order_id: razorpay.orderId, errorCode: 'CHECKOUT_DISMISSED', errorDescription: 'Payment window closed before completion' })
            .catch(() => { /* best effort */ });
          setFailure('Payment was cancelled. Your cart is saved — you can try again.');
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (response) => {
      setBusy(false);
      const err = (response && response.error) || {};
      billingApi
        .reportPaymentFailure({
          razorpay_order_id: (err.metadata && err.metadata.order_id) || razorpay.orderId,
          razorpay_payment_id: err.metadata ? err.metadata.payment_id : undefined,
          errorCode: err.code || 'PAYMENT_FAILED',
          errorDescription: err.description || 'Payment failed',
        })
        .catch(() => { /* best effort */ });
      setFailure(err.description ? `Payment failed: ${err.description}` : 'Payment failed. Your cart is saved — you can try again.');
    });
    rzp.open();
  }, [user, navigate, refreshAll, handleAuthRequired]);

  const handlePay = async () => {
    if (!terms) { setTermsError(true); return; }
    setBusy(true);
    setError('');
    setFailure('');
    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        setError('The payment gateway could not be loaded. Please check your connection and try again.');
        setBusy(false);
        return;
      }
      const res = await billingApi.checkout({ idempotencyKey: idempotencyKey.current });
      if (!res || !res.order || !res.razorpay) {
        setError('Checkout did not return a payable order. Please try again.');
        setBusy(false);
        return;
      }
      setServerOrder(res.order);
      openRazorpay(res.order, res.razorpay);
    } catch (e) {
      setBusy(false);
      if (e && e.status === 401) { handleAuthRequired(); return; }
      setError((e && e.message) || 'We could not start this payment. Please try again.');
    }
  };

  const billingRows = [
    { label: 'Name', value: user.fullName || user.name || '—' },
    { label: 'Email', value: user.email || '—' },
    { label: 'Phone', value: user.phone || user.mobile || user.contact || '—' },
    { label: 'State', value: (serverOrder && serverOrder.customerState) || user.state || user.customerState || '—' },
  ];

  const showSkeleton = !cart && loading;

  return (
    <PageContainer maxWidth={1140}>
      <PageHeader
        icon={<ReceiptLongRoundedIcon />}
        eyebrow="TSG Nexus"
        title="Checkout"
        description="Confirm your order and pay securely. Credits are added to your account only after the payment is verified on our servers."
        action={
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/nextgen-builder/cart')}
            sx={{
              borderRadius: TOKENS.radius.control, fontWeight: 700, textTransform: 'none',
              color: TOKENS.text.caption, border: `1px solid ${TOKENS.border}`, px: 2,
              '&:hover': { bgcolor: TOKENS.bg.page },
            }}
          >
            Back to cart
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: TOKENS.radius.control }} onClose={() => setError('')}>{error}</Alert>}
      {cartError && <Alert severity="error" sx={{ mb: 3, borderRadius: TOKENS.radius.control }} onClose={() => setCartError('')}>{cartError}</Alert>}

      {failure && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: TOKENS.radius.control }}
          action={<Button size="small" onClick={handlePay} disabled={busy} sx={{ fontWeight: 800, textTransform: 'none' }}>Retry payment</Button>}
        >
          {failure}
        </Alert>
      )}

      {showSkeleton ? (
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={260} sx={{ borderRadius: TOKENS.radius.card }} />
          <Skeleton variant="rectangular" height={180} sx={{ borderRadius: TOKENS.radius.card }} />
        </Stack>
      ) : !view || view.lines.length === 0 ? (
        <SectionCard component={motion.div} {...fadeUp}>
          <EmptyState
            icon={<ShoppingCartRoundedIcon />}
            title="Nothing to check out"
            description="Your cart is empty. Add feature credits before checking out."
            action={
              <GradientButton onClick={() => navigate('/nextgen-builder/plans')} sx={{ textTransform: 'none' }}>
                Browse feature credits
              </GradientButton>
            }
          />
        </SectionCard>
      ) : (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' }, alignItems: 'start' }}>
          <Stack spacing={3}>
            {/* ── Order summary (read only) ── */}
            <SectionCard component={motion.div} {...fadeUp} padding={0} sx={{ p: 0, overflow: 'hidden' }}>
              <Box sx={{ px: { xs: 2.25, md: 3 }, pt: 2.5, pb: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.98rem' }}>
                  Order summary
                </Typography>
                {serverOrder && (
                  <Chip
                    size="small"
                    label={`Order ${serverOrder.orderNumber}`}
                    sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem', bgcolor: 'rgba(108,59,255,0.1)', color: TOKENS.brand.primary, border: '1px solid rgba(108,59,255,0.22)' }}
                  />
                )}
              </Box>
              <Divider />

              <TableContainer sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 800, color: TOKENS.text.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' } }}>
                      <TableCell>Product</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell align="right">Unit price</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right" sx={{ width: 48 }} aria-label="Actions" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {view.lines.map((line) => (
                      <TableRow key={line.id} sx={{ '& td': { borderBottom: `1px solid ${TOKENS.borderSoft}`, py: 1.5 } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.86rem' }}>
                            {line.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600, display: 'block' }}>
                            {describeLineUnits(line)}{line.technologyTitle ? ` · ${line.technologyTitle}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: TOKENS.text.secondary, fontSize: '0.84rem' }}>
                            {formatPaise(line.unitPricePaise)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {line.cartItemId ? (
                            <QuantityStepper
                              value={line.quantity}
                              max={maxQtyFor(line.productCode)}
                              disabled={!canEdit || itemBusy || line.unavailable}
                              onChange={(q) => handleQuantity({ id: line.cartItemId }, q)}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 700, color: TOKENS.text.secondary, fontSize: '0.84rem' }}>
                              {line.quantity}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.88rem' }}>
                            {formatPaise(line.subtotalPaise)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {line.cartItemId && (
                            <Tooltip title="Remove">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Remove item"
                                  disabled={!canEdit || itemBusy}
                                  onClick={() => handleRemove({ id: line.cartItemId })}
                                  sx={{ color: TOKENS.text.muted, '&:hover': { color: '#DC2626', bgcolor: 'rgba(239,68,68,0.08)' } }}
                                >
                                  {busyItemId === line.cartItemId ? <CircularProgress size={16} /> : <DeleteOutlineRoundedIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Mobile lines */}
              <Stack sx={{ display: { xs: 'flex', sm: 'none' } }} divider={<Divider />}>
                {view.lines.map((line) => (
                  <Box key={line.id} sx={{ p: 2.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary }}>{line.productName}</Typography>
                    <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600, display: 'block', mt: 0.25 }}>
                      {describeLineUnits(line)}{line.technologyTitle ? ` · ${line.technologyTitle}` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, gap: 1, flexWrap: 'wrap' }}>
                      {line.cartItemId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <QuantityStepper
                            value={line.quantity}
                            max={maxQtyFor(line.productCode)}
                            disabled={!canEdit || itemBusy || line.unavailable}
                            onChange={(q) => handleQuantity({ id: line.cartItemId }, q)}
                          />
                          <IconButton size="small" aria-label="Remove item" disabled={!canEdit || itemBusy} onClick={() => handleRemove({ id: line.cartItemId })} sx={{ color: TOKENS.text.muted }}>
                            {busyItemId === line.cartItemId ? <CircularProgress size={16} /> : <DeleteOutlineRoundedIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600 }}>
                          {formatPaise(line.unitPricePaise)} × {line.quantity}
                        </Typography>
                      )}
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', fontWeight: 600 }}>
                          {formatPaise(line.unitPricePaise)} × {line.quantity}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary }}>
                          {formatPaise(line.subtotalPaise)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>

              {view.fromServer && (
                <Alert severity="info" sx={{ mx: { xs: 2.25, md: 3 }, mb: 2.5, borderRadius: TOKENS.radius.control }}>
                  These are the confirmed totals from our servers for order {serverOrder.orderNumber}. Prices are recalculated at checkout, so they may differ from what your cart showed.
                </Alert>
              )}
            </SectionCard>

            {/* ── Billing details ── */}
            <SectionCard component={motion.div} {...fadeUp}>
              <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.98rem', mb: 2 }}>
                Billing details
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' } }}>
                {billingRows.map((row) => (
                  <Box
                    key={row.label}
                    sx={{ p: 1.4, borderRadius: TOKENS.radius.control, background: TOKENS.bg.page, border: `1px solid ${TOKENS.borderSoft}` }}
                  >
                    <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.64rem', display: 'block' }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TOKENS.text.primary, fontWeight: 700, mt: 0.25, wordBreak: 'break-word' }}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: TOKENS.text.muted, fontWeight: 500 }}>
                Your invoice is issued to these details. Update them in your profile before paying if anything is wrong.
              </Typography>
            </SectionCard>
          </Stack>

          {/* ── Pay panel ── */}
          <SectionCard component={motion.div} {...fadeUp} sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
            <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.98rem', mb: 2 }}>
              Payable now
            </Typography>
            <Stack spacing={1.1}>
              <SummaryLine label="Subtotal" value={formatPaise(view.subtotalPaise)} />
              <SummaryLine label={view.taxLabel} value={formatPaise(view.taxPaise)} />
            </Stack>
            <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
            <SummaryLine label="Grand total" value={formatPaise(view.grandTotalPaise)} strong />

            <Box
              sx={{
                mt: 2.5, p: 1.5, borderRadius: TOKENS.radius.control,
                background: 'rgba(6,182,212,0.07)', border: '1px solid rgba(6,182,212,0.2)',
                display: 'flex', gap: 1.1,
              }}
            >
              <ShieldRoundedIcon sx={{ fontSize: 18, color: '#0E7490', flexShrink: 0, mt: '1px' }} />
              <Typography variant="caption" sx={{ color: '#0E7490', fontWeight: 600, lineHeight: 1.5 }}>
                Payments are processed by Razorpay; entitlements are added only after the payment is verified on our servers.
              </Typography>
            </Box>

            <FormControlLabel
              sx={{ alignItems: 'flex-start', mt: 2, mr: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={terms}
                  onChange={(e) => { setTerms(e.target.checked); if (e.target.checked) setTermsError(false); }}
                  sx={{ pt: 0.25, color: termsError ? '#DC2626' : TOKENS.text.muted, '&.Mui-checked': { color: TOKENS.brand.primary } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: TOKENS.text.caption, fontWeight: 500, lineHeight: 1.5 }}>
                  I agree to the{' '}
                  <MuiLink component={RouterLink} to="/terms-of-service" target="_blank" sx={{ color: TOKENS.brand.primary, fontWeight: 700, textDecoration: 'none' }}>
                    Terms of Service
                  </MuiLink>{' '}
                  and{' '}
                  <MuiLink component={RouterLink} to="/privacy-policy" target="_blank" sx={{ color: TOKENS.brand.primary, fontWeight: 700, textDecoration: 'none' }}>
                    Privacy Policy
                  </MuiLink>
                  .
                </Typography>
              }
            />
            {termsError && (
              <Typography variant="caption" sx={{ display: 'block', color: '#DC2626', fontWeight: 700, mt: 0.25 }}>
                Please accept the terms to continue.
              </Typography>
            )}

            <GradientButton
              fullWidth
              onClick={handlePay}
              disabled={busy || hasUnavailable || view.lines.length === 0}
              endIcon={null}
              startIcon={busy ? <CircularProgress size={15} sx={{ color: '#FFFFFF' }} /> : <LockRoundedIcon sx={{ fontSize: '17px !important' }} />}
              sx={{ mt: 2, py: 1.35, fontSize: '0.95rem', textTransform: 'none', '&.Mui-disabled': { background: '#E5E7EB', color: '#9CA3AF', boxShadow: 'none' } }}
            >
              Proceed to Payment
            </GradientButton>

            {hasUnavailable && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: '#B45309', fontWeight: 700 }}>
                Remove the unavailable items in your cart before paying.
              </Typography>
            )}
          </SectionCard>
        </Box>
      )}
    </PageContainer>
  );
};

export default NextGenCheckout;
